import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { BarChart3, Loader2, LayoutDashboard, Hash, DollarSign, ListOrdered } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, LineChart, Line
} from "recharts";

const API = process.env.REACT_APP_BACKEND_URL;
const CHART_COLORS = ["#6366f1", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#14b8a6", "#f97316", "#06b6d4", "#84cc16"];
const OP_LABELS = { count: "Contar", sum: "Sumar", avg: "Promedio", min: "Minimo", max: "Maximo" };

function formatNumber(num) {
  if (num == null || isNaN(num)) return "0";
  return num.toLocaleString("es-DO", { maximumFractionDigits: 2 });
}

function formatKpiValue(num, formato, label) {
  if (num == null || isNaN(num)) return "0";
  const formatted = num.toLocaleString("es-DO", { maximumFractionDigits: 2 });
  if (formato === "moneda") {
    if (label && /US\$/i.test(label)) return `US$ ${formatted}`;
    if (label && /RD\$/i.test(label)) return `RD$ ${formatted}`;
    return formatted;
  }
  if (formato === "porcentaje") return `${formatted}%`;
  return formatted;
}

function formatAxis(num) {
  if (num == null || isNaN(num)) return "0";
  if (Math.abs(num) >= 1e9) return (num / 1e9).toFixed(1) + "B";
  if (Math.abs(num) >= 1e6) return (num / 1e6).toFixed(1) + "M";
  if (Math.abs(num) >= 1e3) return (num / 1e3).toFixed(0) + "K";
  return num.toLocaleString("es-DO", { maximumFractionDigits: 0 });
}

export default function PublicDashboardBIPage() {
  const { token } = useParams();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/api/dashboards-bi/public/${token}`);
        setDashboard(res.data);
      } catch {
        setError("Dashboard no encontrado o el enlace ha expirado.");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [token]);

  if (loading) return <div className="flex items-center justify-center min-h-screen bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-indigo-500" /></div>;
  if (error) return <div className="flex items-center justify-center min-h-screen bg-slate-50"><div className="text-center"><LayoutDashboard className="w-12 h-12 mx-auto text-slate-300 mb-3" /><p className="text-slate-500">{error}</p></div></div>;

  const charts = (dashboard.charts || []).sort((a, b) => a.order - b.order);

  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="public-dashboard">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-indigo-500" />
            {dashboard.nombre}
          </h1>
          {dashboard.descripcion && <p className="text-sm text-slate-500 mt-1">{dashboard.descripcion}</p>}
          <Badge variant="secondary" className="mt-2 text-[10px]">Vista Pública</Badge>
        </div>

        <div className="grid grid-cols-4 gap-4">
          {charts.map(chart => (
            <div key={chart.id} className={chart.size === "full" ? "col-span-4" : chart.size === "half" ? "col-span-2" : "col-span-1"}>
              <PublicChartCard chart={chart} token={token} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PublicChartCard({ chart, token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.post(`${API}/api/dashboards-bi/public/${token}/aggregate`, {
          coleccion_id: chart.coleccion_id, columna_x: chart.columna_x,
          columna_y: chart.columna_y, operacion: chart.operacion, tipo: chart.tipo, limit: chart.limit || 20,
          segmentar_por: chart.segmentar_por || "",
        });
        setData(res.data);
      } catch { setData(null); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [chart, token]);

  if (loading) return <Card><CardContent className="flex items-center justify-center h-32"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></CardContent></Card>;

  if (chart.tipo === "kpi") {
    const segments = data?.segments;
    const formato = chart.formato || "numero";
    const subtitulo = chart.subtitulo || "";
    const opLabel = OP_LABELS[chart.operacion] || chart.operacion;
    return (
      <Card className="overflow-hidden" data-testid={`public-chart-${chart.id}`}>
        <div className="w-full h-1" style={{ backgroundColor: chart.color || "#6366f1" }} />
        <CardContent className="p-5 pt-4">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{chart.titulo}</p>
              {subtitulo && <p className="text-[10px] text-slate-400 mt-0.5">{subtitulo}</p>}
            </div>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: (chart.color || "#6366f1") + "15" }}>
              {chart.operacion === "count" ? <ListOrdered className="w-4 h-4" style={{ color: chart.color || "#6366f1" }} /> :
               <DollarSign className="w-4 h-4" style={{ color: chart.color || "#6366f1" }} />}
            </div>
          </div>
          {segments && segments.length > 0 ? (
            <div className="mt-3 space-y-2">
              {segments.map((seg, i) => (
                <div key={i} className="flex items-baseline justify-between border-b border-slate-100 pb-1.5 last:border-0">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: chart.color || "#6366f1", opacity: 1 - (i * 0.3) }} />
                    <span className="text-[11px] font-medium text-slate-500">{seg.label}</span>
                    {chart.mostrar_conteo !== false && <span className="text-[9px] text-slate-300">({formatNumber(seg.count)} reg.)</span>}
                  </div>
                  <span className="text-lg font-bold tabular-nums" style={{ color: chart.color || "#6366f1" }}>
                    {formatKpiValue(seg.value, formato, seg.label)}
                  </span>
                </div>
              ))}
              <div className="flex items-baseline justify-between pt-1 border-t border-slate-200">
                <span className="text-[10px] font-semibold text-slate-400 uppercase">Total</span>
                <span className="text-sm font-bold text-slate-700 tabular-nums">{formatKpiValue(data?.value || 0, formato, "")}</span>
              </div>
            </div>
          ) : (
            <div className="mt-3">
              <p className="text-3xl font-bold tabular-nums" style={{ color: chart.color || "#6366f1" }}>{formatKpiValue(data?.value || 0, formato, "")}</p>
              {chart.columna_y && <p className="text-[10px] text-slate-400 mt-1">{opLabel} de {chart.columna_y}</p>}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  const chartData = (data?.labels || []).map((label, i) => ({
    name: label.length > 25 ? label.slice(0, 25) + "..." : label,
    fullName: label,
    value: data.values[i] || 0,
  }));
  const colors = chart.colores || CHART_COLORS;

  return (
    <Card data-testid={`public-chart-${chart.id}`}>
      <CardContent className="p-4">
        <p className="text-sm font-semibold text-slate-700 mb-3">{chart.titulo}</p>
        {chartData.length === 0 ? (
          <p className="text-xs text-slate-400 text-center py-8">Sin datos</p>
        ) : chart.tipo === "bar" ? (
          <ResponsiveContainer width="100%" height={chart.size === "full" ? 350 : 280}>
            <BarChart data={chartData} margin={{ left: 10, right: 10, bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#64748b" }} angle={-35} textAnchor="end" interval={0} height={60} />
              <YAxis tick={{ fontSize: 10, fill: "#64748b" }} tickFormatter={formatAxis} />
              <Tooltip formatter={(val) => [formatNumber(val), chart.columna_y || "Cantidad"]} />
              <Bar dataKey="value" fill={chart.color || "#6366f1"} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : chart.tipo === "line" ? (
          <ResponsiveContainer width="100%" height={chart.size === "full" ? 350 : 280}>
            <LineChart data={chartData} margin={{ left: 10, right: 10, bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#64748b" }} angle={-35} textAnchor="end" interval={0} height={60} />
              <YAxis tick={{ fontSize: 10, fill: "#64748b" }} tickFormatter={formatAxis} />
              <Tooltip formatter={(val) => [formatNumber(val), chart.columna_y || "Cantidad"]} />
              <Line type="monotone" dataKey="value" stroke={chart.color || "#6366f1"} strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={chartData} cx="50%" cy="50%" innerRadius={chart.tipo === "donut" ? 50 : 0} outerRadius={100} paddingAngle={2} dataKey="value" nameKey="name">
                {chartData.map((_, i) => <Cell key={i} fill={colors[i % colors.length]} />)}
              </Pie>
              <Tooltip formatter={(val, name) => [formatNumber(val), name]} contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }} />
            </PieChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}
